import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../../services/api';
import { customerService } from '../../services/customerService';
import { usePermissions } from '../../contexts/PermissionsContext';

/**
 * Kolejka "kontrahenci do sparowania".
 *
 * Backend grupuje faktury bez kontrahent_id po parze (dostawca_nazwa, dostawca_nip),
 * dzieki czemu 402 faktury 'SFD' to JEDEN wiersz i jedno klikniecie.
 * Przypisanie NIE dotyka snapshotow na dokumentach - zapisuje wylacznie
 * kontrahent_id oraz alias nazwy, zeby kolejne faktury trafialy juz same.
 */

const ENDPOINT_LISTA = '/purchase-invoices/kontrahenci-do-sparowania';
const ENDPOINT_PRZYPISZ = '/purchase-invoices/przypisz-kontrahenta';

const thStyle = {
  padding: '0.4rem 0.5rem', textAlign: 'left', fontSize: '10px', fontWeight: '600',
  color: 'var(--text-muted)', borderBottom: '2px solid var(--border)',
  position: 'sticky', top: 0, backgroundColor: 'var(--bg)', zIndex: 1
};
const tdStyle = { padding: '0.4rem 0.5rem', fontSize: '12px', borderBottom: '1px solid var(--border)', verticalAlign: 'top' };

const btnBazowy = {
  padding: '4px 10px', fontSize: '11px', cursor: 'pointer', borderRadius: '4px',
  backgroundColor: 'var(--card-bg)', color: 'var(--text)', border: '1px solid var(--border)'
};

const inputStyle = {
  width: '100%', padding: '4px 10px', fontSize: '12px', borderRadius: '4px',
  border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text)'
};

/** Klucz wiersza - para (nazwa, NIP) jest tym, po czym backend grupuje. */
const kluczGrupy = (g) => `${(g.dostawca_nazwa || '').trim()}||${(g.dostawca_nip || '').trim()}`;

const nazwaKarty = (k) =>
  k?.nazwa_firmy || k?.name || k?.nazwa || `${k?.imie || ''} ${k?.nazwisko || ''}`.trim() || 'Bez nazwy';

const formatKwota = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n.toFixed(2) : '0.00';
};

const KontrahenciDoSparowaniaModal = ({ onClose, onZmiana = null }) => {
  const { hasPermission } = usePermissions();
  const mozeDodawacKarty = hasPermission('klienci', 'dodawanie');

  const [grupy, setGrupy] = useState([]);
  const [ladowanie, setLadowanie] = useState(true);
  const [blad, setBlad] = useState(null);

  // Rozwiniety wiersz + jego wyszukiwarka kontrahenta
  const [aktywnyKlucz, setAktywnyKlucz] = useState(null);
  const [zapytanie, setZapytanie] = useState('');
  const [wyniki, setWyniki] = useState([]);
  const [szukam, setSzukam] = useState(false);
  const [wybranaKarta, setWybranaKarta] = useState(null);
  const [zapisuje, setZapisuje] = useState(false);

  const wczytaj = useCallback(async () => {
    try {
      setLadowanie(true);
      setBlad(null);
      const res = await api.get(ENDPOINT_LISTA);
      const body = res.data?.data ?? res.data;
      const lista = Array.isArray(body) ? body : (body?.items || body?.grupy || []);
      const posortowane = [...lista].sort(
        (a, b) => (parseInt(b.liczba_faktur, 10) || 0) - (parseInt(a.liczba_faktur, 10) || 0)
      );
      setGrupy(posortowane);
    } catch (e) {
      setBlad(e?.message || 'Nie udalo sie pobrac listy faktur bez kontrahenta');
      setGrupy([]);
    } finally {
      setLadowanie(false);
    }
  }, []);

  useEffect(() => { wczytaj(); }, [wczytaj]);

  // Wyszukiwarka kontrahenta - debounce, rola 'dostawca'
  useEffect(() => {
    if (zapytanie.trim().length < 2) {
      setWyniki([]);
      return undefined;
    }
    const tid = setTimeout(async () => {
      try {
        setSzukam(true);
        const odp = await customerService.searchCustomers(zapytanie.trim(), 15, 'dostawca');
        const body = odp?.data ?? odp;
        const lista = Array.isArray(body) ? body : (body?.customers || body?.items || []);
        setWyniki(Array.isArray(lista) ? lista : []);
      } catch {
        setWyniki([]);
      } finally {
        setSzukam(false);
      }
    }, 300);
    return () => clearTimeout(tid);
  }, [zapytanie]);

  const otworzWiersz = (grupa) => {
    const klucz = kluczGrupy(grupa);
    if (aktywnyKlucz === klucz) {
      setAktywnyKlucz(null);
      return;
    }
    setAktywnyKlucz(klucz);
    setZapytanie(grupa.dostawca_nazwa || '');
    setWybranaKarta(null);
    setWyniki([]);
  };

  /**
   * Hurtowe przypisanie calej grupy faktur.
   * @param {Object} grupa   wiersz kolejki
   * @param {Object} karta   wybrana karta z kartoteki (albo null przy zakladaniu nowej)
   * @param {boolean} nowaKarta czy backend ma zalozyc karte z danych faktury
   */
  const przypisz = async (grupa, karta, nowaKarta = false) => {
    if (zapisuje) return;
    const liczba = parseInt(grupa.liczba_faktur, 10) || 0;
    const nazwaCelu = nowaKarta ? (grupa.dostawca_nazwa || 'nowa karta') : nazwaKarty(karta);

    // Operacja hurtowa - potwierdzenie z liczba faktur jest obowiazkowe.
    const pytanie = nowaKarta
      ? `Założyć nową kartę kontrahenta "${grupa.dostawca_nazwa || ''}" (NIP ${grupa.dostawca_nip || 'brak'}) i przypisać do niej ${liczba} faktur?`
      : `Przypisać ${liczba} faktur dostawcy "${grupa.dostawca_nazwa || ''}" do karty "${nazwaCelu}"?`;
    if (!window.confirm(pytanie)) return;

    try {
      setZapisuje(true);
      const payload = {
        dostawca_nazwa: grupa.dostawca_nazwa || '',
        dostawca_nip: grupa.dostawca_nip || ''
      };
      if (nowaKarta) {
        payload.utworz_karte = true;
        payload.nazwa = grupa.dostawca_nazwa || '';
        payload.nip = grupa.dostawca_nip || '';
      } else {
        payload.klient_id = karta.id;
      }

      const res = await api.post(ENDPOINT_PRZYPISZ, payload);
      const dane = res.data;
      if (dane?.success === false) {
        toast.error(dane.message || dane.error || 'Nie udalo sie przypisac faktur');
        return;
      }

      const przypisano = dane?.data?.przypisano ?? dane?.data?.updated ?? liczba;
      const nazwaFinalna = dane?.data?.kontrahent?.nazwa_firmy || dane?.data?.nazwa || nazwaCelu;
      toast.success(`Przypisano ${przypisano} faktur do ${nazwaFinalna}`);

      const klucz = kluczGrupy(grupa);
      setGrupy(prev => prev.filter(g => kluczGrupy(g) !== klucz));
      setAktywnyKlucz(null);
      setWybranaKarta(null);
      setZapytanie('');
      setWyniki([]);
      if (onZmiana) onZmiana();
    } catch (e) {
      toast.error(e?.message || 'Blad przypisania kontrahenta');
    } finally {
      setZapisuje(false);
    }
  };

  const sumaFaktur = grupy.reduce((s, g) => s + (parseInt(g.liczba_faktur, 10) || 0), 0);

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '1rem'
    }}>
      <div style={{
        backgroundColor: 'var(--card-bg)', borderRadius: '8px', width: '100%', maxWidth: '1100px',
        maxHeight: '90vh', display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden'
      }}>
        {/* Naglowek */}
        <div style={{
          padding: '1rem', borderBottom: '1px solid var(--border)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <div>
            <h5 style={{ margin: 0, fontSize: '14px', fontWeight: '700' }}>
              Faktury bez kontrahenta {'—'} do sparowania
            </h5>
            <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
              {grupy.length} dostawc{'ó'}w z faktur | {sumaFaktur} faktur bez powi{'ą'}zania z kartotek{'ą'}
            </div>
          </div>
          <button onClick={onClose} style={{ ...btnBazowy, border: 'none', fontSize: '20px', padding: '0 6px' }}>{'×'}</button>
        </div>

        {/* Tresc - minHeight:0 jest konieczne, inaczej lista rozpycha modal
            i przewijanie przestaje dzialac (ten sam blad juz tu wystapil). */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0.75rem' }}>
          {ladowanie && (
            <div style={{ padding: '2rem', textAlign: 'center', fontSize: '12px', color: 'var(--text-muted)' }}>
              {'⏳'} {'Ł'}adowanie kolejki...
            </div>
          )}

          {!ladowanie && blad && (
            <div style={{
              padding: '0.5rem 0.75rem', backgroundColor: 'var(--danger-light)',
              border: '1px solid var(--danger)', color: 'var(--danger)',
              borderRadius: '4px', fontSize: '12px', fontWeight: '600'
            }}>
              {'⛔'} {blad}
            </div>
          )}

          {!ladowanie && !blad && grupy.length === 0 && (
            <div style={{
              padding: '1.5rem', textAlign: 'center', fontSize: '12px', color: 'var(--success)',
              backgroundColor: 'var(--success-light)', border: '1px solid var(--success)', borderRadius: '4px'
            }}>
              {'✅'} Wszystkie faktury maj{'ą'} przypisanego kontrahenta.
            </div>
          )}

          {!ladowanie && !blad && grupy.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Nazwa dostawcy z faktury</th>
                  <th style={thStyle}>NIP</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Liczba faktur</th>
                  <th style={thStyle}>Zakres dat</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Warto{'ść'}</th>
                  <th style={thStyle}>Propozycja</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Akcje</th>
                </tr>
              </thead>
              <tbody>
                {grupy.map((g) => {
                  const klucz = kluczGrupy(g);
                  const otwarty = aktywnyKlucz === klucz;
                  const kandydaci = Array.isArray(g.kandydaci) ? g.kandydaci : [];
                  const liczba = parseInt(g.liczba_faktur, 10) || 0;
                  return (
                    <React.Fragment key={klucz}>
                      <tr style={{ backgroundColor: otwarty ? 'var(--bg)' : 'transparent' }}>
                        <td style={{ ...tdStyle, fontWeight: '600' }}>{g.dostawca_nazwa || '(pusta nazwa)'}</td>
                        <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{g.dostawca_nip || '-'}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '700' }}>{liczba}</td>
                        <td style={{ ...tdStyle, fontSize: '11px', color: 'var(--text-muted)' }}>
                          {(g.data_od || g.pierwsza_data || '-')} {'–'} {(g.data_do || g.ostatnia_data || '-')}
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>
                          {formatKwota(g.wartosc ?? g.suma_brutto ?? g.wartosc_brutto)} z{'ł'}
                        </td>
                        <td style={tdStyle}>
                          {kandydaci.length === 0 ? (
                            <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>brak</span>
                          ) : (
                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                              {kandydaci.map(k => (
                                <button
                                  key={k.id}
                                  type="button"
                                  disabled={zapisuje}
                                  onClick={() => przypisz(g, k)}
                                  title={`Przypisz wszystkie ${liczba} faktur do tej karty`}
                                  style={{
                                    padding: '1px 6px', fontSize: '10px', borderRadius: '10px', cursor: 'pointer',
                                    backgroundColor: 'var(--purple-light)', color: 'var(--purple)',
                                    border: '1px solid var(--purple)'
                                  }}
                                >
                                  {nazwaKarty(k)}{k.nip ? ` (${k.nip})` : ''}
                                </button>
                              ))}
                            </div>
                          )}
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>
                          <button
                            type="button"
                            onClick={() => otworzWiersz(g)}
                            style={{
                              ...btnBazowy,
                              backgroundColor: otwarty ? 'var(--primary)' : 'var(--card-bg)',
                              color: otwarty ? '#fff' : 'var(--primary)',
                              border: '1px solid var(--primary)'
                            }}
                          >
                            {otwarty ? 'Zamknij' : 'Sparuj'}
                          </button>
                        </td>
                      </tr>

                      {otwarty && (
                        <tr>
                          <td colSpan={7} style={{ padding: '0.5rem 0.75rem 0.75rem', backgroundColor: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                              <div style={{ flex: '1 1 320px', minWidth: 0 }}>
                                <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '2px' }}>
                                  Wyszukaj kontrahenta w kartotece
                                </label>
                                <input
                                  type="text"
                                  value={zapytanie}
                                  onChange={(e) => { setZapytanie(e.target.value); setWybranaKarta(null); }}
                                  placeholder="Nazwa, NIP lub numer karty..."
                                  style={inputStyle}
                                />
                                {szukam && (
                                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>Szukam...</div>
                                )}
                                {wyniki.length > 0 && (
                                  <div style={{
                                    marginTop: '4px', border: '1px solid var(--border)', borderRadius: '4px',
                                    maxHeight: '150px', overflowY: 'auto', backgroundColor: 'var(--card-bg)'
                                  }}>
                                    {wyniki.map(k => {
                                      const aktywna = wybranaKarta?.id === k.id;
                                      return (
                                        <div
                                          key={k.id}
                                          onClick={() => setWybranaKarta(k)}
                                          style={{
                                            padding: '4px 10px', cursor: 'pointer', fontSize: '12px',
                                            borderBottom: '1px solid var(--border)',
                                            backgroundColor: aktywna ? 'var(--primary)' : 'var(--card-bg)',
                                            color: aktywna ? '#fff' : 'var(--text)'
                                          }}
                                        >
                                          <div style={{ fontWeight: '600' }}>{nazwaKarty(k)}</div>
                                          <div style={{ fontSize: '10px', color: aktywna ? '#fff' : 'var(--text-muted)' }}>
                                            {k.numer_klienta ? `nr ${k.numer_klienta} | ` : ''}
                                            {k.nip ? `NIP: ${k.nip}` : 'bez NIP'}
                                            {k.miasto ? ` | ${k.miasto}` : ''}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>

                              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', paddingTop: '14px', flexWrap: 'wrap' }}>
                                <button
                                  type="button"
                                  disabled={!wybranaKarta || zapisuje}
                                  onClick={() => przypisz(g, wybranaKarta)}
                                  style={{
                                    padding: '6px 12px', fontSize: '11px', fontWeight: '600', borderRadius: '4px',
                                    cursor: (!wybranaKarta || zapisuje) ? 'not-allowed' : 'pointer',
                                    backgroundColor: (!wybranaKarta || zapisuje) ? 'var(--bg)' : 'var(--success)',
                                    color: (!wybranaKarta || zapisuje) ? 'var(--text)' : '#fff',
                                    border: `1px solid ${(!wybranaKarta || zapisuje) ? 'var(--border)' : 'var(--success)'}`
                                  }}
                                >
                                  {zapisuje ? 'Zapisywanie...' : `Przypisz wszystkie (${liczba})`}
                                </button>

                                {mozeDodawacKarty && (
                                  <button
                                    type="button"
                                    disabled={zapisuje}
                                    onClick={() => przypisz(g, null, true)}
                                    title="Zaklada karte kontrahenta z danych faktury i przypisuje do niej cala grupe"
                                    style={{
                                      ...btnBazowy,
                                      border: '1px solid var(--info)', color: 'var(--info)'
                                    }}
                                  >
                                    Za{'ł'}{'ó'}{'ż'} now{'ą'} kart{'ę'}
                                  </button>
                                )}
                              </div>
                            </div>

                            <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '6px' }}>
                              Przypisanie zmienia wy{'łą'}cznie powi{'ą'}zanie z kartotek{'ą'}. Nazwa i NIP
                              zapisane na fakturach zostaj{'ą'} nietkni{'ę'}te.
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Stopka */}
        <div style={{
          padding: '0.75rem 1rem', borderTop: '1px solid var(--border)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <button type="button" onClick={wczytaj} disabled={ladowanie} style={btnBazowy}>
            Od{'ś'}wie{'ż'}
          </button>
          <button
            type="button"
            onClick={onClose}
            style={{ ...btnBazowy, padding: '6px 12px', fontSize: '12px', color: 'var(--text-secondary)' }}
          >
            Zamknij
          </button>
        </div>
      </div>
    </div>
  );
};

export default KontrahenciDoSparowaniaModal;
